import React from 'react';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getSystemFlag } from '@/pages/dashboard/pages/overview/_config/system';
import { getMediaChannelIcon, mediaChannelMap } from '@/pages/home/_config/mediaChannal';
import Default from '@/pages/home/assets/svg/dashboard/default.svg';
import { formatNum, formatPercentNum, getRatioText, getIsErrorValue } from '@/pages/dashboard/utils/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { OverflowBubbleText } from '@/pages/dashboard/components/OverflowBubbleText';

interface IRenderCountryCellParams {
  record: any;
  regionDataType: string;
  getFilterItemText: (regionDataType: string, code: string, name: string, isDownload?: boolean) => any;
}

/**
 * 文本溢出检测组件（兼容旧导出名）
 */
export const TextWithOverflowBubble: React.FC<{ text: string; }> = ({ text }) => (
  <OverflowBubbleText text={text} placement="right" dark className="register-table-country-text" />
);

/**
 * 渲染国家/平台列
 */
export const renderCountryCell = ({ record, regionDataType, getFilterItemText }: IRenderCountryCellParams): JSX.Element => {
  let imgDom: JSX.Element;
  let systemImgDom: JSX.Element;
  let channelImgDom: JSX.Element;
  const isOther = record.name === 'Others';
  try {
    imgDom = <img src={getCountryFlag(record?.name)} alt="flag" className='register-table-country-img' />;
  } catch (error) {
    imgDom = <img src={getCountryFlag('undefined')} alt="flag" />;
  }
  try {
    systemImgDom = record?.full_name?.toLowerCase()?.trim() === 'others' ? <></> :
      <img className='country-icon' src={getSystemFlag(record?.full_name)} alt="flag" />;
  } catch (error) {
    systemImgDom = <img className='country-icon' src={getSystemFlag('undefined')} alt="flag" />;
  }
  const channelIcon = getMediaChannelIcon(record?.name || '');
  channelImgDom = <img className='country-icon' src={channelIcon === mediaChannelMap._default ? Default : channelIcon} alt="channel" />;

  // 获取完整文本内容
  const fullText = getFilterItemText(
    regionDataType === 'network_type' ? 'network' : regionDataType, 
    record?.name, 
    record?.full_name
  );

  return (
    <div className='register-table-country'>
      {(regionDataType === 'country' && !isOther) && imgDom}
      {(regionDataType === 'os' && !isOther) && systemImgDom}
      {(regionDataType === 'ua_network' && !isOther) && channelImgDom}
      {(record?.name || record?.name !== null) ? (
        <TextWithOverflowBubble text={fullText} />
      ) : (
        <div className='register-table-country-desc'>-</div>
      )}
    </div>
  );
};

interface IRenderValueCellParams {
  record: any;
  formatRule?: string;
  changeFormatRule?: string;
  isUserQuota: boolean;
  valueName?: string;
  unit?: string;
  percentMetricsList?: string[];
}

/**
 * 渲染数值列（支持配置的格式化规则）
 */
export const renderValueCell = ({
  record,
  formatRule,
  changeFormatRule,
  isUserQuota,
  valueName,
  unit,
  percentMetricsList,
}: IRenderValueCellParams): JSX.Element => {
  const { text, status } = getRatioText(record?.change, false);

  // 格式化指标值
  let value: string;
  if (formatRule) {
    // 使用配置的格式化规则
    value = String(dataFormat({ value: record?.value, formatRule }));
  } else if (percentMetricsList?.includes((valueName || '') + (unit || ''))) {
    // 原有逻辑：百分比指标
    value = String(formatPercentNum(record?.value, 2));
  } else {
    // 原有逻辑：普通数值
    value = String(formatNum(record?.value, false, isUserQuota));
  }

  // 格式化变化率
  let changeText = text;
  if (changeFormatRule && record?.change !== undefined && record?.change !== null) {
    changeText = String(dataFormat({ value: record?.change, formatRule: changeFormatRule }));
  }

  // 处理空值情况
  const isAllNUll = ((status === 'zero' || status === '') && value === '-');
  return (
    <>
      {isAllNUll ? (
        <div className='register-table-daily-grey'>-</div>
      ) : (
        <div className="register-table-daily">
          <div className="register-table-daily-textsum">{value}</div>
          <div className={
            status === 'down' ?
              'register-table-daily-red'
              : status === '' || status === 'zero' ? 'register-table-daily-grey' : 'register-table-daily-green'
          }>
            {changeText}
            {
              status === '' || status === 'no' ? '' : status === 'zero' ? <div className='register-table-daily-line' /> : <SvgIcon type={status === 'down' ? 'red-down-new' : 'green-up-new'} className='register-table-daily-icon' />
            }
          </div>
        </div>
      )}
    </>
  );
};

interface IRenderShareCellParams {
  record: any;
  shareFormatRule?: string;
}

/**
 * 渲染占比列（支持配置的格式化规则）
 */
export const renderShareCell = ({ record, shareFormatRule }: IRenderShareCellParams): JSX.Element => {
  const shareValue = record?.share_of_all;
  if (!shareValue || shareValue === '' || shareValue === '-') {
    return <span>-</span>;
  }

  // 如果配置了格式化规则，使用配置的规则
  if (shareFormatRule) {
    try {
      const formattedShare = dataFormat({
        value: parseFloat(String(shareValue).trim()),
        formatRule: shareFormatRule
      });
      return <span>{String(formattedShare)}</span>;
    } catch (error) {
      // 如果格式化失败，使用原有逻辑
      return <span>{formatPercentNum(parseFloat(String(shareValue).trim()), 1)}</span>;
    }
  }

  // 原有逻辑
  if (getIsErrorValue(shareValue)) {
    return <span>-</span>;
  }
  return <span>{formatPercentNum(parseFloat(String(shareValue).trim()), 1)}</span>;
};

